/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import {
  flatten,
  isEmpty,
  map,
  path,
  pipe,
  pluck,
  values
} from 'ramda';
import React from 'react';
import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Flex,
  Heading,
  Picker,
  Item,
  ListBox,
  Section,
  Switch,
  Tabs,
  TabList,
  Text,
  TextField
} from '@adobe/react-spectrum';
import { defineMessages, injectIntl } from '@nebula/izzy/intl';
import Icon from '@react/react-spectrum/Icon';
import TaskList from '@spectrum-icons/workflow/TaskList';
import ViewList from '@spectrum-icons/workflow/ViewList';
import styles from './styles/index.styl';

const MSG = defineMessages({
  cancel: {
    id: 'gemini.workspace.cancel',
    defaultMessage: 'Cancel'
  },
  close: {
    id: 'gemini.workspaces.close',
    defaultMessage: 'Close'
  },
  delete: {
    id: 'gemini.workspaces.delete',
    defaultMessage: 'Delete'
  },
  enabledViews: {
    id: 'gemini.workspaces.enabledViews',
    defaultMessage: 'Enabled Views'
  },
  errorMessage: {
    id: 'gemini.workspaces.errorMessage',
    defaultMessage: 'Workspace name must be unique.'
  },
  save: {
    id: 'gemini.workspaces.save',
    defaultMessage: 'Save'
  },
  select: {
    id: 'gemini.workspaces.select',
    defaultMessage: 'Select'
  },
  saveToOrg: {
    id: 'gemini.workspaces.saveToOrg',
    defaultMessage: 'Save As Org Workspace'
  },
  workspaceDefault: {
    id: 'gemini.workspaces.default',
    defaultMessage: 'Defaults'
  },
  workspaceNamePlaceholder: {
    id: 'gemini.workspaces.namePlaceholder',
    defaultMessage: 'Workspace Name'
  },
  workspacePlaceholder: {
    id: 'gemini.workspaces.placeholder',
    defaultMessage: 'Select a workspace...'
  },
  workspaceOrg: {
    id: 'gemini.workspaces.orgCustom',
    defaultMessage: 'Org Workspaces'
  },
  workspaceUser: {
    id: 'gemini.workspaces.userCustom',
    defaultMessage: 'User Workspaces'
  }
});

const ViewPluginListBox = ({
  disabled = false,
  izzy,
  onSelectPlugins,
  plugins,
  selectedPlugins
}) => {
  if (!plugins.length) { return null; }
  const disabledKeys = !disabled ? [] : pipe(values, flatten, pluck('namespace'))(plugins);
  return (
    <ListBox
      disabledKeys={disabledKeys}
      onSelectionChange={onSelectPlugins}
      selectedKeys={selectedPlugins}
      width="100%"
      aria-label={izzy(MSG.enabledViews)}
      selectionMode="multiple"
    >
      {!isEmpty(plugins) && map((categoryPlugins) => {
        const category = path([0, 'category'], categoryPlugins);

        return (
          <Section
            key={category}
            title={category}
          >
            {map(({ description, displayName, icon, namespace }) => (
              <Item
                key={namespace}
                description={description}
                value={namespace}
                textValue={displayName}
              >
                {Icon && (
                  <Icon
                    className={styles.icon}
                    size="S"
                  >
                    <img alt={displayName} src={icon} />
                  </Icon>
                )}
                <Text>{displayName}</Text>
              </Item>
            ), categoryPlugins)}
          </Section>
        );
      }, plugins)}
    </ListBox>
  );
};

const WorkspacePicker = ({
  izzy,
  onSelectWorkspace,
  selectedWorkspace,
  showOrgWorkspaces,
  showUserWorkspaces,
  workspaces
}) => (
  <Picker
    aria-label={izzy(MSG.workspacePlaceholder)}
    description={izzy(MSG.workspacePlaceholder)}
    onSelectionChange={onSelectWorkspace}
    selectedKey={selectedWorkspace}
  >
    <Section
      items={workspaces.default}
      title={izzy(MSG.workspaceDefault)}
    >
      {item => <Item>{item.name}</Item>}
    </Section>
    {showOrgWorkspaces && (
      <Section
        items={workspaces.org || []}
        title={izzy(MSG.workspaceOrg)}
      >
        {item => <Item>{item.name}</Item>}
      </Section>
    )}
    {showUserWorkspaces && (
      <Section
        items={workspaces.user || []}
        title={izzy(MSG.workspaceUser)}
      >
        {item => <Item>{item.name}</Item>}
      </Section>
    )}
    {(showOrgWorkspaces || showUserWorkspaces) && (
      <Section>
        <Item key="new">Add a new workspace...</Item>
      </Section>
    )}
  </Picker>
);

const WorkspacesModal = ({
  activeWorkspace,
  editable = false,
  enabledViewPlugins,
  error,
  isDirty = false,
  izzy,
  onChangeWorkspaceName,
  onChangeSaveToOrg,
  onClose,
  onCreateWorkspace,
  onDeleteWorkspace,
  onUpdateWorkspace,
  onPressSelect,
  onSelectViewPlugins,
  onSelectSetting,
  onSelectWorkspace,
  open,
  saveToOrg = false,
  saving = false,
  selectedWorkspace,
  selectedSetting = 'viewPlugins',
  settingsValidationWidget,
  showOrgWorkspaces = true,
  showUserWorkspaces = true,
  viewPlugins,
  workspaceName,
  workspaces
}) => {
  const isNew = selectedWorkspace === 'new';
  // can't delete fallback workspaces until we go all in on workspaces
  const canDelete = editable && selectedWorkspace !== activeWorkspace
    && selectedWorkspace !== 'my-workspace' && selectedWorkspace !== 'my-org-workspace';

  return (
    <DialogContainer>
      {open && (
        <Dialog minHeight="600px" minWidth="900px" type="fullscreen">
          <Heading>Workspaces</Heading>
          <Divider />
          <Content>
            <Flex direction="row" gap="size-400" height="100%">
              <div>
                <WorkspacePicker
                  izzy={izzy}
                  onSelectWorkspace={onSelectWorkspace}
                  selectedWorkspace={selectedWorkspace}
                  showOrgWorkspaces={showOrgWorkspaces}
                  showUserWorkspaces={showUserWorkspaces}
                  workspaces={workspaces}
                />
                {selectedWorkspace && (
                  <React.Fragment>
                    {isNew && (
                      <Flex direction="column" gap="16px" marginY="16px">
                        <TextField
                          aria-label={izzy(MSG.workspaceNamePlaceholder)}
                          description={izzy(MSG.workspaceNamePlaceholder)}
                          errorMessage={error && izzy(MSG.errorMessage)}
                          onChange={onChangeWorkspaceName}
                          isQuiet
                          validationState={error ? 'invalid' : workspaceName ? 'valid' : null}
                          value={workspaceName}
                        />
                        {showOrgWorkspaces && (
                          <Switch
                            isDisabled={!showUserWorkspaces}
                            isEmphasized
                            isSelected={!showUserWorkspaces || saveToOrg}
                            onChange={onChangeSaveToOrg}
                          >
                            {izzy(MSG.saveToOrg)}
                          </Switch>
                        )}
                      </Flex>
                    )}
                    <Tabs
                      marginTop="16px"
                      onSelectionChange={v => onSelectSetting(v)}
                      orientation="vertical"
                      selectedKey={selectedSetting}
                    >
                      <TabList>
                        <Item key="viewPlugins">
                          <ViewList size="S" />
                          <Text id="setting-viewPlugins">Views</Text>
                        </Item>
                        <Item key="validationPlugins">
                          <TaskList size="S" />
                          <Text id="setting-validationPlugins">Validations</Text>
                        </Item>
                      </TabList>
                    </Tabs>
                  </React.Fragment>
                )}
              </div>
              <Divider orientation="vertical" size="M" height="auto" />
              {selectedWorkspace && (
                <div className={styles.settingsContent}>
                  {selectedSetting === 'viewPlugins' && (
                    <ViewPluginListBox
                      disabled={!editable}
                      izzy={izzy}
                      onSelectPlugins={onSelectViewPlugins}
                      plugins={viewPlugins}
                      selectedPlugins={enabledViewPlugins}
                    />
                  )}
                  {selectedSetting === 'validationPlugins' && settingsValidationWidget}
                </div>
              )}
            </Flex>
          </Content>
          <ButtonGroup>
            {<Button onPress={onClose} variant="secondary">{izzy(MSG.close)}</Button>}
            {canDelete && !isNew && (<Button onPress={onDeleteWorkspace} variant="negative">{izzy(MSG.delete)}</Button>)}
            {isDirty ? (
              <Button
                isDisabled={saving}
                onPress={() => (isNew ? onCreateWorkspace() : onUpdateWorkspace())}
                variant="cta"
              >
                {izzy(MSG.save)}
              </Button>
            ) : selectedWorkspace && !isNew && selectedWorkspace !== activeWorkspace ? (
              <Button onPress={onPressSelect} variant="cta">{izzy(MSG.select)}</Button>
            ) : null}
          </ButtonGroup>
        </Dialog>
      )}
    </DialogContainer>
  );
};

WorkspacesModal.displayName = 'WorkspacesModal';
export default injectIntl(WorkspacesModal);
